import { Modal } from '@components/Modal';
import { memo, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AnimatedSpinner } from '../../../../components/AnimatedSpinner';
import { useCompanionChatAnalytics } from '../../../../features/AvatarSession/hooks/useCompanionChatAnalytics';
import { useDataChannel } from '../../../../features/AvatarSession/hooks/useDataChannel';
import { useSessionWebRTC } from '../../../../features/AvatarSession/hooks/useSessionWebRTC';
import { useSessionFeatureState } from '../../../../hooks/storeHooks/useSessionState';
import { useMicrophones } from '../../../../hooks/useMicrophones';
// import { Controls } from '../../../../pages/SessionPage/Controls/Controls';
import { SessionMentor } from '@types/mentor';
import { Controls } from '../../../../features/AvatarSession/components/Controls';
import { Heading } from '../Heading';

interface AvatarDisplayProps {
  avatarId: string;
  mentorId: string;
  slug: string;
  isDeleting: boolean;
  isPublishing?: boolean;
  onPublish: () => void;
  onRestart: () => void;
}

const AvatarDisplay: React.FC<AvatarDisplayProps> = memo(
  ({
    avatarId,
    mentorId,
    slug,
    isPublishing = false,
    isDeleting,
    onPublish,
    onRestart,
  }) => {
    const { t } = useTranslation();
    const videoRef = useRef<HTMLVideoElement>(null);
    const audioRef = useRef<HTMLAudioElement>(null);
    const [avatarReady, setAvatarReady] = useState(false);
    const [showRestartModal, setShowRestartModal] = useState(false);
    const [userClosedSession, setUserClosedSession] = useState(false);
    const {
      stream: userMicrophoneStream,
      selectedMicrophone,
      isMuted,
      connectMicrophone,
      muteMicrophone,
    } = useMicrophones(false);

    const { onMessage } = useDataChannel();
    const { sessionId } = useSessionFeatureState();

    // Set up WebRTC message handler using useDataChannel
    useEffect(() => {
      const unsubscribe = onMessage((data) => {
        switch (data.event) {
          case 'avatar_state_changed':
            if (data.data?.state === 'ready') {
              setAvatarReady(true);
            }
            break;
          default:
            break;
        }
      });

      return () => {
        unsubscribe();
      };
    }, [onMessage]);

    // Analytics: track only companion chat sessions in this preview UI
    useCompanionChatAnalytics({
      enabled: true,
      sessionId: sessionId || null,
      onMessage,
      entryPoint: 'Twin Publish',
      companion: { mentorId, slug },
      isUserClosed: userClosedSession,
    });

    const handleRestartClick = () => {
      setShowRestartModal(true);
    };

    const handleConfirmRestart = () => {
      // Mark that user explicitly closed the session
      setUserClosedSession(true);
      onRestart();
    };

    const handlePublishClick = () => {
      // Mark that user explicitly closed the session
      setUserClosedSession(true);
      onPublish();
    };

    const handleCloseModal = () => {
      if (!isDeleting) {
        setShowRestartModal(false);
      }
    };

    useEffect(
      () => () => {
        setShowRestartModal(false);
      },
      [],
    );

    const { videoStream, audioStream, startWebrtc, stopWebrtc } =
      useSessionWebRTC({
        avatarId,
        mentorId,
        userMicrophoneStream,
        selectedMicrophone,
        context: '',
        companionSlug: slug,
        negotiateType: 'default',
        isCapabilitySession: false,
      });

    // Connect microphone on mount
    useEffect(() => {
      connectMicrophone().catch((error) => {
        console.error('Error connecting microphone:', error);
      });
    }, []);

    // Set video stream
    useEffect(() => {
      if (videoRef.current) {
        videoRef.current.srcObject = videoStream || null;
      }
    }, [videoStream]);

    // Set audio stream
    useEffect(() => {
      if (audioRef.current) {
        audioRef.current.srcObject = audioStream || null;
      }
    }, [audioStream]);

    useEffect(() => {
      startWebrtc();
      return () => {
        stopWebrtc();
      };
    }, []);

    return (
      <div className="w-[750px] max-w-full mx-auto mb-auto sm:mb-0">
        <Heading
          title={t('twin.reviewTwin')}
          subtitle={t('twin.reviewTwinSubtitle')}
        />
        <div className="relative w-full object-cover aspect-video rounded-2xl tc-mv-blur overflow-hidden group">
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            disablePictureInPicture
            data-testid="avatar-main-video"
            className="w-full h-full object-cover aspect-video rounded-2xl"
          />
          {audioStream && <audio ref={audioRef} autoPlay />}

          {avatarReady && (
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex items-center gap-4">
              <Controls
                isMuted={isMuted}
                audioRef={audioRef}
                setMuted={muteMicrophone}
                showScreenShareButton={false}
                isConnected={!!videoStream}
                handleConnect={() => {}}
                handleDisconnect={() => {}}
                companion={{} as SessionMentor}
                disableEndButton
                disableUploadButton
                controls
                isInterview
              />
            </div>
          )}
          {!avatarReady && (
            <div className="start-experience-btn loader !absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
              <span className="button-content relative">
                <span className="star-icon star-first">✦</span>
                <span className="star-icon star-second large">✦</span>
                <span className="star-icon star-third">✦</span>
                <span className="text ms-2">{t('twin.connecting')}</span>
              </span>
            </div>
          )}
        </div>
        <div className="flex gap-4 justify-center my-8">
          <button
            data-testid="avatar-restart-btn"
            className="btn btn-secondary"
            onClick={handleRestartClick}
            disabled={!avatarReady || isDeleting}>
            <i className="fa-solid fa-rotate-right mr-2" /> {t('twin.restart')}
          </button>
          <button
            data-testid="avatar-publish-btn"
            className="btn btn-primary"
            onClick={handlePublishClick}
            disabled={isPublishing}>
            {isPublishing ? 'Publishing...' : t('twin.publish')}{' '}
            <i className="fa-solid fa-arrow-right ml-2" />
          </button>
        </div>

        <Modal
          title={t('twin.restartCreation')}
          onClose={handleCloseModal}
          isOpen={showRestartModal}>
          <>
            <p className="text-white py-4 px-6">
              {t('twin.restartConfirmation')}
            </p>
            <div className="flex justify-end gap-4 border-t border-white/10 py-4 px-6">
              <button
                data-testid="avatar-restart-modal-cancel-btn"
                onClick={handleCloseModal}
                disabled={isDeleting}
                className="btn btn-secondary">
                {t('common.cancel')}
              </button>
              <button
                data-testid="avatar-restart-modal-confirm-btn"
                onClick={handleConfirmRestart}
                disabled={isDeleting}
                className="btn bg-red-600 text-white rounded-md hover:bg-red-700">
                {isDeleting && <AnimatedSpinner className="mr-2" />}
                {t('twin.restart')}
              </button>
            </div>
          </>
        </Modal>
      </div>
    );
  },
);

export { AvatarDisplay };
